import { useCallback, useEffect, useState } from "react";
import { Save, Settings as Cog, Cloud, CheckCircle2, ExternalLink, X, Loader2, FolderPlus } from "lucide-react";
import { api, errorToMessage } from "@/lib/apiClient";
import { V1 } from "@/constants/testIds";
import { canCampaignsWrite, useAuth } from "@/lib/auth";

const SECTIONS = [
  { key: "company_profile", label: "Company Details",
    fields: [["legal_name","Legal name","text"],["gstin","GSTIN","text"],
              ["state","State (code)","text"],["city","City","text"],
              ["pincode","Pincode","text"],["phone","Phone","text"],
              ["email","Email","text"],["logo_url","Logo URL","text"]] },
  { key: "gst", label: "GST",
    fields: [["seller_state","Seller state","text"],
              ["default_gst_rate_pct","Default GST rate (%)","number"]] },
  { key: "invoice_series", label: "Invoice Series",
    fields: [["prefix","Prefix","text"],["pad","Pad","number"],
              ["next_number","Next number","number"]] },
  { key: "sales_series", label: "Sales Series",
    fields: [["prefix","Prefix","text"],["pad","Pad","number"],
              ["next_number","Next number","number"]] },
  { key: "purchase_series", label: "Purchase Series",
    fields: [["prefix","Prefix","text"],["pad","Pad","number"],
              ["next_number","Next number","number"]] },
  { key: "business_hours", label: "Business Hours",
    fields: [["open","Open (HH:MM)","text"],["close","Close (HH:MM)","text"]] },
  { key: "notifications", label: "Notifications (flags only in V1.0)",
    fields: [["email_enabled","Email enabled","checkbox"],
              ["sms_enabled","SMS enabled","checkbox"]] },
  { key: "razorpay", label: "Razorpay (read-only mirror)",
    readOnly: true,
    fields: [["mode","Mode","text"],["key_id","Key ID","text"]] },
  { key: "google_drive", label: "Google Drive Backup",
    custom: "google_drive" },
  { key: "backup_schedule", label: "Backup Schedule",
    fields: [["enabled","Enabled","checkbox"],
              ["cron","Cron expression","text"],
              ["keep_last","Retention (last N)","number"],
              ["upload_to_drive","Upload to Google Drive","checkbox"]] },
  { key: "general", label: "General",
    fields: [["timezone","Timezone","text"],
              ["currency","Currency","text"],
              ["date_format","Date format","text"]] },
];

function GoogleDriveSection({ canWrite }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [folderInput, setFolderInput] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings/google-drive/status");
      setStatus(data);
      setFolderInput(data.folder_id || "");
    } catch {
      setStatus({ connected: false, has_client_config: false });
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Refresh after the OAuth popup posts back its result.
  useEffect(() => {
    const h = (e) => { if (e?.data?.type === "gd_oauth_done") load(); };
    window.addEventListener("message", h);
    return () => window.removeEventListener("message", h);
  }, [load]);

  const flash = (m) => { setNotice(m); setTimeout(() => setNotice(null), 2500); };

  const connect = async () => {
    if (!canWrite) return;
    setBusy(true);
    setNotice(null);
    try {
      const { data } = await api.post("/settings/google-drive/authorize-url");
      window.open(data.authorize_url, "gd_oauth",
                  "width=520,height=640,noopener=no,noreferrer=no");
    } catch (e) {
      flash(`⚠ ${errorToMessage(e, "Could not start OAuth")}`);
    } finally {
      setBusy(false);
    }
  };

  const saveFolder = async () => {
    if (!canWrite || !folderInput.trim()) return;
    setBusy(true);
    setNotice(null);
    try {
      const { data } = await api.post("/settings/google-drive/folder",
                                       { folder: folderInput.trim() });
      flash(data.created ? "Folder created" : "Folder saved");
      await load();
    } catch (e) {
      flash(`⚠ ${errorToMessage(e, "Save failed")}`);
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!canWrite) return;
    if (!window.confirm("Disconnect Google Drive? Backups already uploaded will remain on Drive.")) return;
    setBusy(true);
    try {
      await api.post("/settings/google-drive/disconnect");
      flash("Disconnected");
      await load();
    } catch (e) {
      flash(`⚠ ${errorToMessage(e, "Disconnect failed")}`);
    } finally { setBusy(false); }
  };

  if (!status) {
    return (
      <section className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm text-sm text-slate-500">
        Loading Google Drive status…
      </section>
    );
  }

  return (
    <section data-testid={V1.settingsSection("google_drive")}
      className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
          <Cloud size={16}/> Google Drive Backup
        </h2>
        {notice && <span className="text-xs text-emerald-700">{notice}</span>}
      </div>

      {!status.has_client_config && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Google OAuth client is not configured on the server yet. Set
          <code className="mx-1 font-mono">GOOGLE_OAUTH_CLIENT_ID</code> and
          <code className="mx-1 font-mono">GOOGLE_OAUTH_CLIENT_SECRET</code> in
          <code className="mx-1 font-mono">backend/.env</code> and restart the backend.
        </div>
      )}

      <div className="mt-4 flex items-center gap-2">
        {status.connected ? (
          <span data-testid="gd-status-connected"
            className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800">
            <CheckCircle2 size={13}/> Connected
            {status.connected_at && <span className="ml-1 text-emerald-700/70">since {new Date(status.connected_at).toLocaleDateString()}</span>}
          </span>
        ) : (
          <span data-testid="gd-status-disconnected"
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600">
            Not connected
          </span>
        )}
        {canWrite && !status.connected && (
          <button data-testid="gd-connect-btn" onClick={connect}
            disabled={busy || !status.has_client_config}
            className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-slate-900 text-white px-3 py-1.5 text-sm hover:bg-slate-800 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin"/> : <ExternalLink size={14}/>}
            Connect Google Drive
          </button>
        )}
        {canWrite && status.connected && (
          <button data-testid="gd-disconnect-btn" onClick={disconnect}
            disabled={busy}
            className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-rose-300 bg-white text-rose-700 px-3 py-1.5 text-sm hover:bg-rose-50 disabled:opacity-50">
            <X size={14}/> Disconnect
          </button>
        )}
      </div>

      {status.connected && (
        <div className="mt-4">
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">
              Backup folder — enter an existing Google Drive <b>Folder ID</b> or a <b>new folder name</b> to create
            </span>
            <div className="flex items-center gap-2">
              <input data-testid="gd-folder-input"
                value={folderInput}
                onChange={(e) => setFolderInput(e.target.value)}
                placeholder="1a2B3cDeF... (ID) or My-Backups (create)"
                disabled={!canWrite || busy}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-50 font-mono" />
              <button data-testid="gd-folder-save"
                onClick={saveFolder}
                disabled={!canWrite || busy || !folderInput.trim()}
                className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 text-white px-3 py-2 text-sm hover:bg-slate-800 disabled:opacity-50">
                <FolderPlus size={14}/> Save
              </button>
            </div>
            {status.folder_id && (
              <span className="mt-1 block text-[11px] text-slate-500 font-mono">Current folder ID: {status.folder_id}</span>
            )}
          </label>
        </div>
      )}
    </section>
  );
}


function Section({ meta, value, canWrite, onSave }) {
  const [form, setForm] = useState(value || {});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => { setForm(value || {}); }, [value]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async () => {
    setBusy(true);
    setNotice(null);
    try {
      await onSave(meta.key, form);
      setNotice("Saved");
      setTimeout(() => setNotice(null), 1500);
    } catch (e) {
      setNotice(`⚠ ${e}`);
    } finally { setBusy(false); }
  };

  return (
    <section data-testid={V1.settingsSection(meta.key)}
      className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-slate-900">{meta.label}</h2>
        {notice && <span className="text-xs text-emerald-700">{notice}</span>}
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        {meta.fields.map(([k, label, type]) => (
          <label key={k} className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">{label}</span>
            {type === "checkbox" ? (
              <input type="checkbox" checked={!!form[k]}
                disabled={!canWrite || meta.readOnly}
                onChange={(e) => upd(k, e.target.checked)}
                className="h-4 w-4" />
            ) : (
              <input type={type} value={form[k] ?? ""}
                disabled={!canWrite || meta.readOnly}
                onChange={(e) => upd(k, type === "number" ? Number(e.target.value) : e.target.value)}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-500" />
            )}
          </label>
        ))}
      </div>
      {canWrite && !meta.readOnly && (
        <div className="mt-4 flex justify-end">
          <button onClick={submit} disabled={busy} data-testid={V1.settingsSaveBtn(meta.key)}
            className="inline-flex items-center gap-1 rounded-md bg-slate-900 text-white px-4 py-1.5 text-sm hover:bg-slate-800 disabled:opacity-50">
            <Save size={14} /> {busy ? "Saving…" : "Save"}
          </button>
        </div>
      )}
    </section>
  );
}

export default function SettingsPage() {
  const { user } = useAuth();
  const canWrite = canCampaignsWrite(user);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setErr(null);
    try {
      const r = await api.get("/settings");
      setData(r.data);
    } catch (e) { setErr(errorToMessage(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onSave = async (key, value) => {
    try {
      await api.put(`/settings/${key}`, { value });
      await load();
    } catch (e) { throw errorToMessage(e, "Save failed"); }
  };

  return (
    <div className="space-y-6" data-testid={V1.settingsPage}>
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2">
          <Cog size={20} /> System Settings
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Company, GST, series, business hours, taxes, notifications, backup schedule.
          {!canWrite && " Read-only for your role."}
        </p>
      </div>

      {err && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{err}</div>
      )}
      {!data && !err && <div className="text-slate-400 text-sm">Loading…</div>}

      {data && SECTIONS.map((meta) => (
        meta.custom === "google_drive"
          ? <GoogleDriveSection key={meta.key} canWrite={canWrite} />
          : <Section key={meta.key} meta={meta}
              value={data[meta.key]} canWrite={canWrite} onSave={onSave} />
      ))}
    </div>
  );
}
